package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.presentation.components.BalanceOverTimeCard
import com.daytimegaming.ourdailyfinances.presentation.components.BalanceTimeframe
import com.daytimegaming.ourdailyfinances.presentation.preview.SecurityHoldingDetailPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import com.daytimegaming.ourdailyfinances.presentation.util.monthDayYearFormat
import com.daytimegaming.ourdailyfinances.presentation.util.toCurrencySymbol
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.time.Clock

@Composable
fun SecurityHoldingDetailScreen(
    accountId: String,
    securityId: String,
    dashboardId: String? = null,
    onNavigateBack: () -> Unit,
    onInvalidAccess: (() -> Unit)? = null,
    viewModel: SecurityHoldingDetailViewModel = koinViewModel(key = "$accountId-$securityId-$dashboardId") {
        parametersOf(accountId, securityId, dashboardId)
    }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(state) {
        if (state is SecurityHoldingDetailScreenState.Error && onInvalidAccess != null) {
            onInvalidAccess()
        }
    }

    SecurityHoldingDetailScreenContent(
        state = state,
        onNavigateBack = onNavigateBack
    )
}

@Composable
fun SecurityHoldingDetailScreenContent(
    state: SecurityHoldingDetailScreenState,
    onNavigateBack: () -> Unit
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(text = "Holding Details") },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            when (val s = state) {
                is SecurityHoldingDetailScreenState.Loading -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }
                is SecurityHoldingDetailScreenState.Error -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(text = s.message, color = MaterialTheme.colorScheme.error)
                    }
                }
                is SecurityHoldingDetailScreenState.Loaded -> {
                    val holding = s.holding
                    val currencySymbol = s.account.isoCurrencyCode.toCurrencySymbol()
                    var selectedTimeframe by remember { mutableStateOf(BalanceTimeframe.SixMonths) }

                    val chartPoints = remember(s.chartPoints, selectedTimeframe) {
                        val today = try {
                            Clock.System.todayIn(TimeZone.currentSystemDefault())
                        } catch (e: Exception) {
                            Clock.System.todayIn(TimeZone.currentSystemDefault())
                        }
                        val startDate = when (selectedTimeframe) {
                            BalanceTimeframe.OneMonth -> today.minus(1, DateTimeUnit.MONTH)
                            BalanceTimeframe.ThreeMonths -> today.minus(3, DateTimeUnit.MONTH)
                            BalanceTimeframe.SixMonths -> today.minus(6, DateTimeUnit.MONTH)
                            BalanceTimeframe.OneYear -> today.minus(1, DateTimeUnit.YEAR)
                            BalanceTimeframe.YearToDate -> LocalDate(today.year, 1, 1)
                        }
                        val holdingVal = holding.institutionValue ?: (holding.quantity * holding.institutionPrice)
                        val history = s.chartPoints
                        val points = mutableListOf<Pair<LocalDate, Double>>()
                        var currentDate = startDate
                        while (currentDate <= today) {
                            val value = history.lastOrNull { it.first <= currentDate }?.second
                                ?: history.firstOrNull()?.second
                                ?: holdingVal
                            points.add(currentDate to value)
                            currentDate = currentDate.plus(1, DateTimeUnit.DAY)
                        }
                        points
                    }

                    val currentBalance = holding.institutionValue ?: (holding.quantity * holding.institutionPrice)
                    val formattedBalance = currentBalance.formatAmount()
                    val parts = formattedBalance.split('.')
                    val intPart = parts.getOrElse(0) { "0" }
                    val decPart = parts.getOrNull(1) ?: "00"

                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        contentPadding = PaddingValues(bottom = 24.dp)
                    ) {
                        // 1. Balance Header
                        item {
                            GlassCard(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(start = 16.dp, end = 16.dp, top = 16.dp, bottom = 4.dp)
                            ) {
                                Text(
                                    text = holding.security?.name ?: "Unknown Security",
                                    style = MaterialTheme.typography.headlineMedium,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    fontWeight = FontWeight.Bold
                                )
                                Spacer(modifier = Modifier.height(2.dp))
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    val ticker = holding.security?.tickerSymbol
                                    ticker?.let {
                                        Box(
                                            modifier = Modifier
                                                .background(
                                                    Color(0x1FBEC6E0),
                                                    shape = RoundedCornerShape(999.dp)
                                                )
                                                .padding(horizontal = 8.dp, vertical = 2.dp)
                                        ) {
                                            Text(
                                                text = it,
                                                style = MaterialTheme.typography.labelSmall,
                                                color = MaterialTheme.colorScheme.secondary
                                            )
                                        }
                                    }
                                    Text(
                                        text = "${holding.quantity.formatAmount()} shares",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }

                                Spacer(modifier = Modifier.height(16.dp))

                                Row(verticalAlignment = Alignment.Bottom) {
                                    Text(
                                        text = currencySymbol,
                                        style = MaterialTheme.typography.titleLarge,
                                        color = MaterialTheme.colorScheme.primary,
                                        modifier = Modifier.padding(bottom = 12.dp)
                                    )
                                    Text(
                                        text = intPart,
                                        style = MaterialTheme.typography.displayLarge.copy(fontSize = 48.sp),
                                        color = MaterialTheme.colorScheme.onSurface,
                                        fontWeight = FontWeight.Bold
                                    )
                                    Text(
                                        text = ".$decPart",
                                        style = MaterialTheme.typography.titleLarge,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        modifier = Modifier.padding(bottom = 8.dp)
                                    )
                                }

                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    text = "Current Price: $currencySymbol${holding.institutionPrice.formatAmount()}",
                                    style = MaterialTheme.typography.labelMedium,
                                    color = MaterialTheme.colorScheme.secondary
                                )
                            }
                        }

                        // 2. Balance Over Time Graph Section
                        item {
                            Column(modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    text = "Value Over Time",
                                    style = MaterialTheme.typography.headlineMedium,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    modifier = Modifier.padding(horizontal = 16.dp)
                                )
                                if (holding.proxySecurity != null) {
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text(
                                        text = "This holding uses a proxy security holding. Historical performance data for these securities may not always be accurate.",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        modifier = Modifier.padding(horizontal = 16.dp)
                                    )
                                }
                                Spacer(modifier = Modifier.height(12.dp))
                                Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                                    BalanceOverTimeCard(
                                        points = chartPoints,
                                        selectedTimeframe = selectedTimeframe,
                                        onTimeframeSelected = { selectedTimeframe = it },
                                        currencySymbol = currencySymbol,
                                        showPeriodChange = false,
                                        showDateLabels = false,
                                        chartHeight = 200.dp,
                                        footnote = null,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(bottom = 16.dp)
                                    )
                                }
                            }
                        }

                        // 3. Price History List Header
                        item {
                            Text(
                                text = "Price History",
                                style = MaterialTheme.typography.headlineMedium,
                                color = MaterialTheme.colorScheme.onSurface,
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp)
                            )
                        }

                        // 4. List of prices and dates
                        if (s.priceHistory.isEmpty()) {
                            item {
                                GlassCard(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                                    Text(
                                        text = "No historical price data found.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            }
                        } else {
                            items(s.priceHistory) { entry ->
                                Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                                    GlassCard(modifier = Modifier.fillMaxWidth()) {
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            horizontalArrangement = Arrangement.SpaceBetween,
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Column {
                                                Text(
                                                    text = entry.date.monthDayYearFormat(),
                                                    style = MaterialTheme.typography.bodyLarge,
                                                    color = MaterialTheme.colorScheme.onSurface,
                                                    fontWeight = FontWeight.Medium
                                                )
                                                Spacer(modifier = Modifier.height(2.dp))
                                                Text(
                                                    text = "${entry.quantity.formatAmount()} shares",
                                                    style = MaterialTheme.typography.bodySmall,
                                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                                )
                                            }
                                            Column(horizontalAlignment = Alignment.End) {
                                                Text(
                                                    text = "$currencySymbol${entry.price.formatAmount()}",
                                                    style = MaterialTheme.typography.headlineMedium,
                                                    color = MaterialTheme.colorScheme.onSurface,
                                                    fontWeight = FontWeight.Bold,
                                                    textAlign = TextAlign.End
                                                )
                                                Spacer(modifier = Modifier.height(2.dp))
                                                Text(
                                                    text = "Value: $currencySymbol${entry.value.formatAmount()}",
                                                    style = MaterialTheme.typography.bodySmall,
                                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                                    textAlign = TextAlign.End
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@PreviewLightDark
@Composable
fun SecurityHoldingDetailScreenPreview(
    @PreviewParameter(SecurityHoldingDetailPreviewParameterProvider::class) state: SecurityHoldingDetailScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            SecurityHoldingDetailScreenContent(
                state = state,
                onNavigateBack = {}
            )
        }
    }
}
